import {
  Card,
  Checkbox,
  MonthPicker,
  UserSelect,
} from '@/components_deprecated'
import { LeaderGroupSelect } from '@/components_deprecated/Select/LeaderGroupSelect'
import { PageType } from '@/modules/common/consts'
import { Field } from '@/modules/companies'
import { getNodes } from '@/utils/graphql'
import { usePermissions, useUser, useLeaderGroups } from '@/utils/hooks'
import { useInfoModal } from '@/utils/hooks/useInfoModal/useInfoModal'
import { PERMISSIONS } from '@/utils/permissions'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { Icon } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, formatISO, startOfMonth } from 'date-fns'
import React, { useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import styled from 'styled-components'
import { AddonOverview } from './AddonOverview'
import { WORK_PERIODS } from './queries'
import { WorkPeriodApproveCards } from './WorkPeriodApproveCards'
import { WorkPeriodApproveTable } from './WorkPeriodApproveTable'
import { H1 } from '@/components'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
  position: relative;
`

const InfoContentWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`

const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;

  margin-bottom: 1rem;
  padding: 0;

  section {
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    gap: 1rem;
    padding: 1rem;

    & + section {
      grid-template-columns: auto;
      border-left: 1px solid ${props => props.theme.colors.gray8};
      align-items: center;
    }
  }

  ${props => props.theme.media.mobile} {
    grid-template-columns: 1fr;

    section {
      grid-template-columns: 1fr;
      padding: 1rem;

      & + section {
        padding-top: 0;
        border-left: 0;
      }
    }
  }
`

const ApprovalCard = styled(Card)`
  &:hover {
    cursor: pointer;
  }
  margin: 0 0 1rem 0;
`

const FlexWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const UserLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const UserLink = styled.span`
  color: ${props => props.theme.colors.primary500};
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    text-decoration: none;
  }
`

export const WorkPeriodApproveView: React.FC = () => {
  const translations = useTranslate({
    salaryCode: 'common.salary-code',
    amount: 'common.amount',
    activities: 'common.activities',
    workDay: 'common.work-day',
    dayRest: 'common.day-rest',
    weekRest: 'common.week-rest',
    showRest: 'common.show-rest',
    export: 'common.export',
    weekN: ['common.week-n', { n: 0 }],
    workOverview: 'salary.work-overview',

    nUsersNeedApproval: ['salary.n-users-need-approval', { n: 0 }],
    oneUserNeedsApproval: 'salary.one-user-needs-approval',
    nWorkPeriods: ['salary.n-work-periods', { n: 0 }],

    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'common.overtime',
    available: 'activities.availability-time',
    lockedValue: 'salary.locked-value',
    lock: 'salary.lock',

    toast: {
      mailSuccess: 'exports.success-email-process',
    },
    error: {
      generic: 'error.generic-server-error',
    },

    salaryMode: 'common.salary-mode',
    month: 'common.month',
    user: 'common.user',
    chooseGroup: 'common.choose-group',

    overtimeThatOverlapsMonths: 'salary.overtime-that-overlaps-months',
    nextMonth: 'common.next-month',
    workPeriods: 'salary.work-periods',
    confirmHours: 'salary.confirm-hours',

    showResolved: 'salary.show-resolved',

    leaderGroups: 'companies.leader-groups',
  })

  const me = useUser()
  const { hasPermissions } = usePermissions()
  const canSeeOthers = hasPermissions(
    PERMISSIONS.workPeriod.view.othersworkperiod,
    true
  )
  const { InfoModal, infoContent, InfoButton } = useInfoModal(
    PageType.CONFIRM_HOURS
  )

  const [remainingApprovalOpen, setRemainingApprovalOpen] = useState(false)

  const { hasAccess, myLeaderGroups, groupsLoading } = useLeaderGroups()

  const form = useForm<{
    user: string | null
    datetimeStart: Date
    datetimeEnd: Date
    showResolved: boolean
    leaderGroup: string | null
    leaderGroupsUsers: string[]
  }>({
    values: {
      user: null,
      datetimeStart: startOfMonth(new Date()),
      datetimeEnd: endOfMonth(new Date()),
      showResolved: false,
      leaderGroup: null,
      leaderGroupsUsers: [],
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'workPeriodApprove',
        storeFormStateKey: 'workPeriodApprove',
        transformValues: {
          user: val => {
            return val && canSeeOthers ? String(val) : null
          },
          datetimeStart: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
          datetimeEnd: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
        },
      },
    },
  })

  const queryVariables = {
    user: form.formValues.user ?? (canSeeOthers ? null : me.id),
    datetimeStart: formatISO(form.formValues.datetimeStart),
    datetimeEnd: formatISO(form.formValues.datetimeEnd),
  }

  const { data, loading, refetch } = useQuery(WORK_PERIODS, {
    variables: queryVariables,
  })

  const workPeriods = getNodes(data?.allWorkPeriods)
  const selectedLeaderGroupUsers = new Set(form.formValues.leaderGroupsUsers)
  const selectedLeaderGroup = form.formValues.leaderGroup

  const displayWorkPeriods = workPeriods
    .filter(wp => {
      if (!selectedLeaderGroup) return true
      return (
        selectedLeaderGroupUsers.has(wp.user.id) ||
        wp.leaderGroup?.id === selectedLeaderGroup
      )
    })
    .filter(wp => {
      if (form.formValues.showResolved) return true
      return !wp.approvedByManager
    })

  const userTasks = data?.remainingWorkPeriodResolving ?? []

  const remainingWorkPeriodResolvingCount = userTasks.length ?? 0

  const changeLeaderGroup = (value: string | null) => {
    const selectedGroup = myLeaderGroups.find(g => g.id === value)
    form.updateForm({
      leaderGroup: value,
      leaderGroupsUsers: selectedGroup?.users.map(user => user.id) ?? [],
    })
  }

  return (
    <Wrapper>
      <InfoContentWrapper>
        <H1>{translations.confirmHours}</H1>
        {infoContent && <InfoButton />}
      </InfoContentWrapper>

      {canSeeOthers && userTasks && remainingWorkPeriodResolvingCount > 0 && (
        <ApprovalCard
          onClick={() => setRemainingApprovalOpen(!remainingApprovalOpen)}
        >
          <FlexWarning>
            <Icon icon="exclamation-triangle" color="orange" />
            <p>
              <b>
                {remainingWorkPeriodResolvingCount === 1
                  ? translations.oneUserNeedsApproval
                  : translations.nUsersNeedApproval({
                      n: remainingWorkPeriodResolvingCount,
                    })}
              </b>
            </p>
          </FlexWarning>
          {remainingApprovalOpen && (
            <UserLinks>
              {userTasks.map(userRemainingCount => (
                <UserLink
                  key={userRemainingCount.user.id}
                  onClick={evt => {
                    evt.stopPropagation()
                    evt.preventDefault()
                    form.updateForm({ user: userRemainingCount.user.id })
                    setRemainingApprovalOpen(false)
                  }}
                >
                  {userRemainingCount.user.fullName} (
                  {translations.nWorkPeriods({
                    n: userRemainingCount.numTasks,
                  })}
                  )
                </UserLink>
              ))}
            </UserLinks>
          )}
        </ApprovalCard>
      )}
      <Controls>
        <section>
          {canSeeOthers && (
            <Field>
              <label>{translations.user}</label>
              <UserSelect
                value={form.formValues.user}
                grayed
                width="100%"
                minWidth={isMobileOnly ? '0' : '260px'}
                height="3.875rem"
                onChange={user => form.updateForm({ user: user ?? null })}
                filterSalaryResponsible={false}
                filterLeaderGroup={true}
                nullable
              />
            </Field>
          )}
          {hasAccess && (
            <Field>
              <label>{translations.leaderGroups}</label>
              <LeaderGroupSelect
                value={form.formValues.leaderGroup}
                loading={groupsLoading}
                options={
                  myLeaderGroups?.map(group => ({
                    label: group.name,
                    value: group.id,
                  })) ?? []
                }
                fullWidth
                grayed={true}
                onChange={value => changeLeaderGroup(value)}
                placeholder={translations.chooseGroup}
                nullable
              />
            </Field>
          )}

          <Field>
            <label>{translations.showResolved}</label>
            <Checkbox
              checked={form.formValues.showResolved}
              onChange={checked => form.updateForm({ showResolved: checked })}
              boxSize="3.875rem"
              padding="2rem"
              boxBorderRadius="8px"
            />
          </Field>
        </section>
        <section>
          <Field>
            <label>{translations.month}</label>
            <MonthPicker
              value={form.formValues.datetimeStart}
              onChange={date => {
                const start = startOfMonth(date)
                const end = endOfMonth(date)
                form.updateForm({
                  datetimeStart: start,
                  datetimeEnd: end,
                })
              }}
            />
          </Field>
        </section>
      </Controls>

      {form.formValues.user !== null && (
        <AddonOverview
          userId={form.formValues.user}
          datetimeStart={form.formValues.datetimeStart}
          datetimeEnd={form.formValues.datetimeEnd}
        />
      )}

      {isMobileOnly ? (
        <WorkPeriodApproveCards
          workPeriods={displayWorkPeriods}
          leaderGroups={myLeaderGroups}
          loading={loading}
          initRefetch={() => refetch()}
        />
      ) : (
        <WorkPeriodApproveTable
          workPeriods={displayWorkPeriods}
          leaderGroups={myLeaderGroups}
          showUserColumn={canSeeOthers}
          loading={loading}
          initRefetch={() => refetch()}
        />
      )}
      {infoContent && <InfoModal />}
    </Wrapper>
  )
}
